import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Paper,
  Divider,
  IconButton
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

interface DocumentMetadataViewModalProps {
  open: boolean;
  onClose: () => void;
  metadata: Record<string, unknown> | null;
  filename?: string;
}

export default function DocumentMetadataViewModal({ 
  open, 
  onClose, 
  metadata, 
  filename 
}: DocumentMetadataViewModalProps) {
  if (!metadata) {
    return null;
  }

  // We filter entries where the key ends with 'Name'
  const filteredEntries = Object.entries(metadata).filter(([key]) => 
    key.endsWith('Name')
  );

  // Helper function to format field names
  const formatFieldName = (key: string): string => {
    switch (key) {
      case 'proFormaName':
        return 'Pro Forma';
      case 'documentTypeName':
        return 'Document Type';
      case 'territoryName':
        return 'Territory';
      default:
        // Convert camelCase to Title Case
        return key
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, str => str.toUpperCase())
          .trim();
    }
  };

  // Helper function to get display value
  const getDisplayValue = (value: unknown): string => {
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Document Metadata</Typography>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: 'grey.500' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ py: 2 }}>
          {filename && (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  DOCUMENT
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {filename}
                </Typography>
              </Box>
              <Divider sx={{ my: 3 }} />
            </>
          )}

          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
            METADATA FIELDS
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {filteredEntries.map(([key, value]) => (
              <Paper key={key} variant="outlined" sx={{ p: 2 }}>
                <Box>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      fontSize: '0.75rem', 
                      textTransform: 'uppercase',
                      fontWeight: 500,
                      mb: 1
                    }}
                  >
                    {formatFieldName(key)}
                  </Typography>
                  <Chip 
                    label={getDisplayValue(value)} 
                    variant="outlined"
                    sx={{ 
                      fontSize: '0.875rem',
                      height: 'auto',
                      py: 1,
                      '& .MuiChip-label': {
                        whiteSpace: 'normal',
                        wordBreak: 'break-word'
                      }
                    }}
                  />
                </Box>
              </Paper>
            ))}
          </Box>

          {filteredEntries.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No matching metadata available for this document.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={onClose} variant="contained" sx={{ minWidth: 100 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}